import { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import RadialChart from './RadialChart';
import "./page_viz.css";

const generateData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return {
        'Enc #': months.map((month) => ({ month, value: 900 + Math.random() * 500 })),
        'Visit #': months.map((month) => ({ month, value: 800 + Math.random() * 800 })),
        'CPT #': months.map((month) => ({ month, value: 2000 + Math.random() * 1000 })),
        'Billed $': months.map((month) => ({ month, value: 200000 + Math.random() * 200000 })),
        'Pay $': months.map((month) => ({ month, value: 250000 + Math.random() * 150000 })),
        'Cont Adj $': months.map((month) => ({ month, value: 400000 + Math.random() * 200000 })),
        'Man Adj $': months.map((month) => ({ month, value: 5000 + Math.random() * 10000 })),
        'Total Adj $': months.map((month) => ({ month, value: 400000 + Math.random() * 300000 })),
        'Ins AR $': months.map((month) => ({ month, value: 20000 + Math.random() * 30000 })),
        'Pat AR $': months.map((month) => ({ month, value: 8000 + Math.random() * 5000 })),
        'Claim Total AR $': months.map((month) => ({ month, value: 30000 + Math.random() * 30000 })),
        'GCR': months.map((month) => ({ month, value: 20 + Math.random() * 50 })),
        'Denial Rate': months.map((month) => ({ month, value: 3 + Math.random() * 7 })),
    };
};

const fieldTitles = {
    'Enc #': 'Encounter Trends',
    'Visit #': 'Visit Trends',
    'CPT #': 'CPT Trends',
    'Billed $': 'Billed Amount Trends',
    'Pay $': 'Payment Trends',
    'Cont Adj $': 'Contract Adjustment Trends',
    'Man Adj $': 'Manual Adjustment Trends',
    'Total Adj $': 'Total Adjustment Trends',
    'Ins AR $': 'Insurance AR Trends',
    'Pat AR $': 'Patient AR Trends',
    'Claim Total AR $': 'Claim Total AR Trends',
    'GCR': 'GCR Trends',
    'Denial Rate': 'Denial Rate Trends',
};

const fieldLabels = {
    'Enc #': 'Encounters',
    'Visit #': 'Visits',
    'CPT #': 'CPT Codes',
    'Billed $': 'Billed',
    'Pay $': 'Payments',
    'Cont Adj $': 'Contract Adj',
    'Man Adj $': 'Manual Adj',
    'Total Adj $': 'Total Adj',
    'Ins AR $': 'Insurance AR',
    'Pat AR $': 'Patient AR',
    'Claim Total AR $': 'Claim AR',
    'GCR': 'GCR',
    'Denial Rate': 'Denial Rate',
};

const fields = [
    'Enc #',
    'Visit #',
    'CPT #',
    'Billed $',
    'Pay $',
    'Cont Adj $',
    'Man Adj $',
    'Total Adj $',
    'Ins AR $',
    'Pat AR $',
    'Claim Total AR $',
    'GCR',
    'Denial Rate',
];

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip">
                <div className="tooltip-month">{payload[0].payload.month}</div>
                <div className="tooltip-value">{payload[0].value.toFixed(0)}</div>
            </div>
        );
    }
    return null;
};

export default function App() {
    const [currentFieldIndex, setCurrentFieldIndex] = useState(1);
    const [selectedMonth, setSelectedMonth] = useState(0);
    const [isFlipping, setIsFlipping] = useState(false);
    const allData = generateData();

    const currentField = fields[currentFieldIndex];
    const currentData = allData[currentField];
    const currentTitle = fieldTitles[currentField];
    const currentLabel = fieldLabels[currentField];

    const handleNext = () => {
        if (currentFieldIndex < fields.length - 1) {
            setIsFlipping(true);
            setTimeout(() => {
                setCurrentFieldIndex(currentFieldIndex + 1);
                setTimeout(() => {
                    setIsFlipping(false);
                }, 50);
            }, 600);
        }
    };

    const handlePrev = () => {
        if (currentFieldIndex > 0) {
            setIsFlipping(true);
            setTimeout(() => {
                setCurrentFieldIndex(currentFieldIndex - 1);
                setTimeout(() => {
                    setIsFlipping(false);
                }, 50);
            }, 600);
        }
    };

    return (
        <div className="app-container">
            <div className="book-container">
                <div className="floating-pages">
                    <div className="floating-page page-1"></div>
                    <div className="floating-page page-2"></div>
                    <div className="floating-page page-3"></div>
                    <div className="floating-page page-4"></div>
                    <div className="floating-page page-5"></div>
                </div>

                <div className="book">
                    <div className="book-shadow-wrap">
                        <div className="book-inner">
                            <div className="book-content-wrapper">
                                <div className="page-header">
                                    <h1 className="page-title">{currentTitle}</h1>
                                    <div className="page-nav-arrows">
                                        <button className="page-arrow">‹</button>
                                        <button className="page-arrow">›</button>
                                    </div>
                                </div>

                                <div className="chart-section">
                                    <div className="chart-label">
                                        <span className="chart-dot"></span>
                                        {currentLabel}
                                    </div>

                                    <div className="chart-wrapper-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart
                                                data={currentData}
                                            >
                                                <defs>
                                                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#4c8bf5" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#4c8bf5" stopOpacity={0.05} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e8edf5" vertical={false} />
                                                <XAxis
                                                    dataKey="month"
                                                    tick={{ fontSize: 12, fill: '#9ca3af' }}
                                                    stroke="#e8edf5"
                                                    axisLine={{ stroke: '#e8edf5' }}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 12, fill: '#9ca3af' }}
                                                    stroke="#e8edf5"
                                                    axisLine={{ stroke: '#e8edf5' }}
                                                    tickLine={false}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Area
                                                    type="monotone"
                                                    dataKey="value"
                                                    stroke="#4c8bf5"
                                                    strokeWidth={2.5}
                                                    fill="url(#colorValue)"
                                                    dot={{ fill: '#4c8bf5', r: 5, strokeWidth: 0 }}
                                                    activeDot={{ r: 7, fill: '#4c8bf5', strokeWidth: 3, stroke: 'white' }}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>

                                    <div className="chart-legend">
                                        <span className="legend-dot"></span>
                                        {currentLabel}
                                    </div>

                                    <div className="month-selector">
                                        {months.map((month, index) => (
                                            <button
                                                key={month}
                                                className={`month-btn ${selectedMonth === index ? 'active' : ''}`}
                                                onClick={() => setSelectedMonth(index)}
                                            >
                                                {month}
                                            </button>
                                        ))}
                                        <button className="month-nav-btn">›</button>
                                    </div>
                                </div>

                                <div className="navigation">
                                    <button
                                        className="nav-btn prev-btn"
                                        onClick={handlePrev}
                                        disabled={currentFieldIndex === 0 || isFlipping}
                                    >
                                        <span className="nav-arrow">‹</span>
                                        Prev
                                    </button>

                                    <div className="page-counter">
                                        {currentFieldIndex + 1} / {fields.length}
                                    </div>

                                    <button
                                        className="nav-btn next-btn"
                                        onClick={handleNext}
                                        disabled={currentFieldIndex === fields.length - 1 || isFlipping}
                                    >
                                        Next
                                        <span className="nav-arrow">›</span>
                                    </button>
                                </div>
                            </div>

                            <div className="book-spine"></div>
                        </div>

                        {isFlipping && (
                            <div className="turning-page">
                                <div className="page-turn-inner">
                                    <div className="page-turn-front"></div>
                                    <div className="page-turn-back"></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="radial-chart-outer">
                <RadialChart />
            </div>
        </div>
    );
}
